import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, FormHelperText, Stack, Typography, Chip, IconButton } from '@mui/material'
import { color } from '../../../theme'
import { IOption } from './ReactHookFormSelect'
import { useReadonly } from '../../../reusableUtils/useReadonly'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

interface IReactHookFormSelectProps {
  label: string
  name: string
  options: IOption[]
  showHelperError?: boolean
  disabled?: boolean
}

const ReactHookFormSelectRedesign = ({ label, name, options, showHelperError = true, disabled }: IReactHookFormSelectProps) => {
  const { control } = useFormContext()
  const isReadonly = useReadonly(name)

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Find the index of the currently selected value in options
        const currentIndex = options.findIndex(o => o.value === field.value)

        // Current option object
        const currentOption = currentIndex >= 0 ? options[currentIndex] : undefined

        // Helper to move to the previous option
        const handlePrev = () => {
          if (currentIndex > 0) {
            field.onChange(options[currentIndex - 1].value)
          }
        }

        // Helper to move to the next option
        const handleNext = () => {
          if (currentIndex < options.length - 1) {
            field.onChange(options[currentIndex + 1].value)
          }
        }

        // If readOnly or disabled, we disable the arrows
        const arrowsDisabled = isReadonly || disabled

        return (
          <Box
            sx={{
              border: `1px solid ${color.grey3}`,
              borderRadius: '12px',
              p: 2,
              width: '100%',
              maxWidth: 250,
              textAlign: 'center',
              position: 'relative',
              padding: '28px'
            }}>
            {/* Top badge (if there's a note on the selected option) */}
            {currentOption?.note && (
              <Chip
                label={currentOption.note}
                sx={{
                  position: 'absolute',
                  textTransform: 'uppercase',
                  top: '-16px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#f7f98f',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              />
            )}

            {/* Title */}
            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>{label}</Typography>

            {/* Value + Arrows */}
            <Stack
              alignItems='center'
              justifyContent='center'
              sx={{
                position: 'relative',
                border: `1.5px solid`,
                borderColor: '#7766B3',
                borderRadius: '50px',
                width: '120px',
                height: '40px',
                mx: 'auto',
                mb: 1
              }}>
              {/* The current value (centered) */}
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: color.stylinkDarkPurple
                }}>
                {currentOption ? currentOption.label : '—'}
              </Typography>

              {/* Left arrow (absolute) */}
              <IconButton
                onClick={handlePrev}
                disabled={arrowsDisabled || currentIndex <= 0}
                sx={{
                  position: 'absolute',
                  left: 0,
                  color: color.stylinkDarkPurple,
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
                }}>
                <KeyboardArrowLeftIcon />
              </IconButton>

              {/* Right arrow (absolute) */}
              <IconButton
                onClick={handleNext}
                disabled={arrowsDisabled || currentIndex >= options.length - 1}
                sx={{
                  position: 'absolute',
                  right: 0,
                  color: color.stylinkDarkPurple,
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
                }}>
                <KeyboardArrowRightIcon />
              </IconButton>
            </Stack>

            {/* Error message */}
            {showHelperError && error?.message && (
              <FormHelperText error sx={{ mt: 1 }}>
                {error.message}
              </FormHelperText>
            )}
          </Box>
        )
      }}
    />
  )
}

export default ReactHookFormSelectRedesign
